/**
 * Aufgabe bearbeiten - Titel und Verknuepfungen (Roadmap F-11).
 *
 * Bisher gab es keinen Weg, eine Aufgabe nach dem Anlegen zu aendern. Dieser
 * Dialog oeffnet sich mit einem Klick auf den Titel, in jeder Ansicht
 * (`TaskRow` -> `useTaskDialog`), und ist einmal in der App-Huelle eingehaengt.
 *
 * Deadline seit F-09: bis wann fertig - getrennt vom eingeplanten Tag.
 * Notizen seit F-05, mit einfacher Formatierung (Absaetze, Aufzaehlungen).
 * Eingeplant seit F-06: Datum, optional Uhrzeit und Dauer - wird im Kalender
 * zum Eintrag oben am Tag bzw. zum Zeitblock (`db/repositories/taskSchedule`).
 *
 * Wiederholung seit F-24: derselbe Editor und dieselbe Regel wie bei Terminen;
 * bei einer Serie fragt Speichern nach "nur dieses Mal / folgende / alle".
 *
 * Verknuepfungsregel: Projekt ODER Ziel. Mit Elternteil wird der Bereich
 * angezeigt statt gewaehlt - er kommt von oben (`domain/links/hierarchy.ts`).
 */
import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';

import { Dialog } from '@/ui/primitives/Dialog';
import { Button } from '@/ui/primitives/Button';
import { Input } from '@/ui/primitives/Input';
import { DeadlineField } from '@/ui/composites/DeadlineField';
import { fromDeadlineValue, toDeadlineValue, type DeadlineValue } from '@/ui/composites/deadlineValue';
import { AreaDot } from '@/modules/areas/AreaDot';
import { useAreaMap, useAreas } from '@/modules/areas/useAreas';
import { useLinkNames } from '@/modules/links/linkNames';
import { useTaskDialog } from '@/modules/tasks/taskDialogStore';
import { db } from '@/db/schema';
import { updateTask } from '@/db/repositories/tasks';
import { setTaskLinks } from '@/db/repositories/links';
import { DEFAULT_BLOCK_MINUTES, scheduleTask } from '@/db/repositories/taskSchedule';
import { PlanFeld, type PlanWert } from '@/modules/tasks/components/PlanFeld';
import { VerknuepfteTermine } from '@/modules/calendar/components/VerknuepfteTermine';
import { RecurrenceEditor } from '@/modules/calendar/components/RecurrenceEditor';
import { fromRrule, toRrule, type RecurrenceValue } from '@/modules/calendar/lib/recurrenceForm';
import { serieFragen } from '@/modules/tasks/serienFrage';
import { aufSerieUebertragen, vorkommenAbspalten, wiederholungSetzen } from '@/db/repositories/aufgabenSerie';
import { deadlineKey } from '@/domain/deadlines';
import { formatDate, today } from '@/lib/datetime';
import { cn } from '@/lib/utils/cn';
import { de } from '@/i18n/de';
import type { CalendarEvent, ID, Task } from '@/domain/types';

export function TaskDialogHost() {
  const taskId = useTaskDialog((s) => s.taskId);
  const close = useTaskDialog((s) => s.close);

  // Das Ergebnis traegt seine ID mit: useLiveQuery liefert beim Wechsel
  // zunaechst noch das ALTE Ergebnis - ohne diesen Abgleich stuende kurz die
  // vorherige Aufgabe im Dialog.
  const geladen = useLiveQuery(
    async () => {
      if (!taskId) return null;
      const task = await db.tasks.get(taskId);
      const block = task?.timeBlockEventId ? await db.events.get(task.timeBlockEventId) : undefined;
      return { id: taskId, task, block };
    },
    [taskId],
  );
  const task = geladen && geladen.id === taskId ? geladen.task : undefined;
  const block = geladen && geladen.id === taskId ? geladen.block : undefined;

  return (
    <Dialog open={task !== undefined} onClose={close} title="Aufgabe bearbeiten" footer={null}>
      {task && <Form key={task.id} task={task} block={block} onClose={close} />}
    </Dialog>
  );
}

function Form({ task, block, onClose }: { task: Task; block?: CalendarEvent; onClose: () => void }) {
  const areas = useAreas();
  const areaMap = useAreaMap();
  const { projects, goals, subjects } = useLinkNames();
  const [subjectId, setSubjectId] = useState<ID | ''>(task.subjectId ?? '');
  // Fach (F-22): aktive Faecher plus das bisherige, falls archiviert.
  const fachOptionen = [...subjects.values()]
    .filter((s) => !s.archived || s.id === task.subjectId)
    .sort((a, b) => a.order - b.order);

  const [titel, setTitel] = useState(task.title);
  const [projectId, setProjectId] = useState<ID | ''>(task.projectId ?? '');
  const [goalId, setGoalId] = useState<ID | ''>(task.goalId ?? '');
  const [areaId, setAreaId] = useState<ID | null>(task.areaId);
  const [frist, setFrist] = useState<DeadlineValue>(toDeadlineValue(task.dueDate, task.dueTime));
  const [notiz, setNotiz] = useState(task.notes ?? '');
  const planAnfang: PlanWert = {
    date: task.plannedDate ?? '',
    start: block ? String(block.start).slice(11, 16) : '',
    minutes: block ? blockMinuten(block) : task.estimateMinutes ?? DEFAULT_BLOCK_MINUTES,
  };
  const [plan, setPlan] = useState<PlanWert>(planAnfang);
  const [wdh, setWdh] = useState<RecurrenceValue>(() => fromRrule(task.wiederholung?.rrule));
  const [saving, setSaving] = useState(false);

  // Nur, was man sinnvoll zuordnet - plus die aktuelle Zuordnung, auch wenn
  // das Projekt inzwischen abgeschlossen ist. Sonst verschwaende sie still.
  const projektOptionen = [...projects.values()]
    .filter((p) => p.status === 'aktiv' || p.status === 'pausiert' || p.id === task.projectId)
    .sort((a, b) => a.title.localeCompare(b.title, 'de'));
  const zielOptionen = [...goals.values()]
    .filter((g) => g.status === 'offen' || g.status === 'laeuft' || g.id === task.goalId)
    .sort((a, b) => a.title.localeCompare(b.title, 'de'));

  const projekt = projectId ? projects.get(projectId) : undefined;
  const direktesZiel = !projekt && goalId ? goals.get(goalId) : undefined;
  const zielUeberProjekt = projekt?.goalId ? goals.get(projekt.goalId) : undefined;

  const bereichQuelle = projekt
    ? `über Projekt „${projekt.title}"`
    : direktesZiel ? `über Ziel „${direktesZiel.title}"` : null;
  const effektiverBereich = projekt ? projekt.areaId : direktesZiel ? direktesZiel.areaId : areaId;
  const bereich = effektiverBereich ? areaMap.get(effektiverBereich) : undefined;

  // Widerspruch zur Frist des Elternteils - live beim Eingeben (F-09).
  const eltern = projekt
    ? { art: 'Projekt', titel: projekt.title, date: projekt.deadline, time: projekt.deadlineTime }
    : direktesZiel
      ? { art: 'Ziel', titel: direktesZiel.title, date: direktesZiel.deadline, time: direktesZiel.deadlineTime }
      : undefined;
  const zuSpaet = Boolean(frist.date && eltern?.date
    && deadlineKey(frist.date, frist.time || undefined) > deadlineKey(eltern.date, eltern.time));

  const trimmed = titel.trim();

  // Wiederholung (F-24): Anker ist der Plantag, sonst die Deadline, sonst heute.
  const ankerAlt = task.plannedDate ?? task.dueDate ?? today();
  const anker = plan.date || frist.date || today();

  async function speichern() {
    if (trimmed === '' || saving) return;
    setSaving(true);
    try {
      const f = fromDeadlineValue(frist);
      const notes = notiz.trim() || undefined;
      const textGeaendert = trimmed !== task.title || f.date !== task.dueDate || f.time !== task.dueTime || notes !== task.notes;
      const datumGeaendert = f.date !== task.dueDate || plan.date !== planAnfang.date;
      const planGeaendert = plan.date !== planAnfang.date || plan.start !== planAnfang.start
        || Boolean(plan.start && plan.minutes !== planAnfang.minutes);
      const linksGeaendert = (projectId || undefined) !== task.projectId || (goalId || undefined) !== task.goalId
        || areaId !== task.areaId || (subjectId || undefined) !== task.subjectId;
      const neueRegel = toRrule(wdh, anker) ?? null;
      const alteRegel = task.wiederholung ? toRrule(fromRrule(task.wiederholung.rrule), ankerAlt) ?? null : null;
      const regelGeaendert = neueRegel !== alteRegel;

      // Bei einer Serie: fuer welches Vorkommen gilt die Aenderung?
      let id = task.id;
      let scope: Awaited<ReturnType<typeof serieFragen>> = null;
      if (task.wiederholung && (textGeaendert || planGeaendert || linksGeaendert || regelGeaendert)) {
        scope = await serieFragen('bearbeiten', regelGeaendert);
        if (!scope) return;
        if (scope === 'dieser') id = (await vorkommenAbspalten(task.id)) ?? task.id;
      }

      if (textGeaendert) {
        // `undefined` entfernt das Feld - so laesst sich eine Deadline oder Notiz loeschen.
        await updateTask(id, { title: trimmed, dueDate: f.date, dueTime: f.time, notes });
      }
      if (planGeaendert) {
        await scheduleTask(id, {
          date: plan.date || null, start: plan.start || undefined, minutes: plan.minutes,
        });
      }
      if ((subjectId || undefined) !== task.subjectId) {
        await updateTask(id, { subjectId: subjectId || undefined });
      }
      await setTaskLinks(id, {
        projectId: projectId || null,
        goalId: goalId || null,
        areaId,
      });
      // Neue Regel oder neue Daten der Serie: neu verankern.
      if (id === task.id && (regelGeaendert || (task.wiederholung && datumGeaendert))) {
        await wiederholungSetzen(task.id, neueRegel);
      }
      if (scope === 'alle') await aufSerieUebertragen(task.id);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Feld label="Titel">
        <Input
          value={titel}
          onChange={(e) => setTitel(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void speichern(); }}
          autoFocus
        />
      </Feld>

      <Feld label="Beschreibung und Notizen" hinweis="Leerzeile = neuer Absatz, „- “ am Zeilenanfang = Aufzählung.">
        <textarea
          value={notiz}
          onChange={(e) => setNotiz(e.target.value)}
          rows={4}
          placeholder="Details, Gedanken, Links …"
          className="w-full resize-y rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-accent focus:outline-none"
        />
      </Feld>

      <Feld label="Eingeplant" hinweis="Wann du daran arbeitest. Mit Uhrzeit wird es ein Block im Kalender.">
        <PlanFeld value={plan} onChange={setPlan} />
      </Feld>

      <Feld label="Wiederholen" hinweis="Für Pflichten mit Termin, z. B. „Zimmer aufräumen jeden Samstag“. Was du regelmäßig tun willst, um dranzubleiben (Serien, Quote), ist eine Gewohnheit.">
        <RecurrenceEditor value={wdh} start={anker} onChange={setWdh} aufgabe />
      </Feld>

      <Feld label="Deadline" hinweis="Bis wann fertig – nicht, wann du daran arbeitest.">
        <DeadlineField value={frist} onChange={setFrist} label="Deadline der Aufgabe" />
        {zuSpaet && eltern?.date && (
          <p role="alert" className="text-[12px] text-warn">
            Endet nach der Deadline von {eltern.art} „{eltern.titel}" ({formatDate(eltern.date)}
            {eltern.time ? `, ${eltern.time}` : ''}).
          </p>
        )}
      </Feld>

      <Feld label="Projekt">
        <Auswahl
          value={projectId}
          onChange={setProjectId}
          leer="Kein Projekt"
          optionen={projektOptionen.map((p) => ({ id: p.id, label: p.title }))}
        />
      </Feld>

      {projekt ? (
        <Feld label="Ziel">
          <p className="text-sm text-ink-muted">
            {zielUeberProjekt
              ? `„${zielUeberProjekt.title}" – über das Projekt`
              : 'Das Projekt zahlt auf kein Ziel ein.'}
          </p>
        </Feld>
      ) : (
        <Feld label="Ziel" hinweis="Für kleine Ziele, die kein eigenes Projekt brauchen.">
          <Auswahl
            value={goalId}
            onChange={setGoalId}
            leer="Kein Ziel"
            optionen={zielOptionen.map((g) => ({ id: g.id, label: g.title }))}
          />
        </Feld>
      )}

      {/* Termine, an denen die Aufgabe haengt (F-04 d) - verknuepft wird im Kalender. */}
      <Feld label="Termine">
        <VerknuepfteTermine filter={{ taskId: task.id }} kompakt onGeoeffnet={onClose}
          leerText="Keine – im Kalender beim Termin unter „Verknüpft mit“ wählen." />
      </Feld>

      {fachOptionen.length > 0 && (
        <Feld label="Fach" hinweis="Macht die Aufgabe zur Hausaufgabe auf der Schulseite.">
          <Auswahl
            value={subjectId}
            onChange={setSubjectId}
            leer="Kein Fach"
            optionen={fachOptionen.map((s) => ({ id: s.id, label: s.name }))}
          />
        </Feld>
      )}

      <Feld label={de.areas.field}>
        {bereichQuelle ? (
          <p className="flex items-center gap-1.5 text-sm text-ink-muted">
            {bereich && <AreaDot color={bereich.color} size="sm" />}
            {bereich?.name ?? de.areas.none} <span className="text-ink-faint">– {bereichQuelle}</span>
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            <Chip aktiv={areaId === null} onClick={() => setAreaId(null)}>{de.areas.none}</Chip>
            {areas.map((a) => (
              <Chip key={a.id} aktiv={areaId === a.id} onClick={() => setAreaId(a.id)}>
                <AreaDot color={a.color} size="sm" />
                {a.name}
              </Chip>
            ))}
          </div>
        )}
      </Feld>

      <div className="flex justify-end gap-2 border-t border-line pt-3">
        <Button variant="ghost" onClick={onClose}>{de.common.cancel}</Button>
        <Button variant="primary" disabled={trimmed === '' || saving} onClick={() => void speichern()}>
          {de.common.save}
        </Button>
      </div>
    </div>
  );
}

function Auswahl({ value, onChange, leer, optionen }: {
  value: string;
  onChange: (id: string) => void;
  leer: string;
  optionen: { id: ID; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 w-full rounded-md border border-line bg-surface px-2 text-sm text-ink focus:border-accent focus:outline-none"
    >
      <option value="">{leer}</option>
      {optionen.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  );
}

function Feld({ label, hinweis, children }: {
  label: string; hinweis?: string; children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{label}</span>
      {children}
      {hinweis && <span className="text-[11px] text-ink-faint">{hinweis}</span>}
    </div>
  );
}

function Chip({ aktiv, onClick, children }: {
  aktiv: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={aktiv}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm transition-colors',
        aktiv
          ? 'border-accent bg-accent/10 text-ink'
          : 'border-line text-ink-muted hover:border-line-strong hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

/** Dauer eines Zeitblocks in Minuten - offsetfrei gerechnet. */
function blockMinuten(e: CalendarEvent): number {
  return Math.round((Date.parse(`${String(e.end)}:00Z`) - Date.parse(`${String(e.start)}:00Z`)) / 60_000);
}
